/* eslint-disable react/button-has-type */
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { connect } from 'react-redux';
import { message, Spin } from 'antd-v5';
import { addDays, endOfDay, format, startOfDay } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingOutlined } from '@ant-design/icons';
import { Section } from '../../components/Section';
import { AppointmentTable } from '../AppointmentTable';
import { assignAssociateToAppointments, completeAppointments, getAppointments } from '../../../../../services/today';
import { APPOINTMENTS_STATUS, getAppointmentsConfig } from '../../../../../services/appointments';
import { AssignAssociatesModal } from '../../../../Layout/AssignAssociatesModal';
import AddEditAppointment from '../../../Clients/QuickView/Appointments/AddEditAppointment';
import { isEditable } from '../helpers/isEditable';

const TodayAppointments = ({ auth, allAssoc, openChatModal }) => {
  const { data, isFetching, refetch } = useQuery(
    ['todayAppointments', auth.userId],
    async () => {
      try {
        const response = await getAppointments({
          query: {
            limit: 5,
            offset: 0,
            sort: 'appointmentDateTime',
            associateIds: [auth.userId],
            statuses: [APPOINTMENTS_STATUS.ASSIGNED],
            fromDate: format(startOfDay(new Date()), 'yyyy-MM-dd'),
            toDate: format(endOfDay(addDays(new Date(), 7)), 'yyyy-MM-dd'),
          },
          loadingStatusOff: true,
        });
        return response?.data;
      } catch (error) {
        console.error('Error fetching today appointments:', error);
        return [];
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: !!auth?.merchantId && !!auth.userId,
    },
  );

  const { data: appointmentsConfig } = useQuery(
    ['appointments-config'],
    async () => {
      return getAppointmentsConfig();
    },
    {
      refetchOnWindowFocus: false,
      initialData: {},
      enabled: !!auth.merchantId,
    },
  );

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const handleSelectedRowKeysChange = useCallback(selected => {
    setSelectedRowKeys(selected);
  }, []);

  const handleComplete = useCallback(async () => {
    try {
      await completeAppointments(selectedRowKeys);
      refetch();
      message.success('Appointments completed successfully');
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('Error completing appointments:', error);
      message.error('Error completing appointments');
    }
  }, [selectedRowKeys, refetch]);

  const handleAssign = useCallback(
    async associateId => {
      try {
        await assignAssociateToAppointments(selectedRowKeys, associateId, true);
        refetch();
        message.success('Appointments assigned successfully');
        setOpenAssignModal(false);
        setSelectedRowKeys([]);
        setSelectedAppointment(null);
      } catch (error) {
        console.error('Error assigning appointments:', error);
        message.error('Error assigning appointments');
      }
    },
    [selectedRowKeys, refetch],
  );

  const handleEdit = useCallback(appointment => {
    setSelectedAppointment(appointment);
    setOpenEditModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenEditModal(false);
    setSelectedAppointment(null);
  }, []);

  const selectedStoreRow = useMemo(() => {
    const selectedAppointments = data?.filter(appointment => selectedRowKeys?.includes(appointment.id));
    return (
      selectedAppointments?.map(app => app?.store?.id).filter((id, index, self) => self.indexOf(id) === index) || []
    );
  }, [selectedRowKeys, data]);

  return (
    <Section
      title="Appointments"
      viewAllUrl="/all-appointments"
      className="m-btm-35"
      actions={
        selectedRowKeys.length > 0 ? (
          <div className="flex-row items-center gap-8">
            <button
              className="flex-row-nospacebetwee-nowrap border-none pointer"
              style={{ alignItems: 'center', backgroundColor: '#fff' }}
              onClick={() => setOpenAssignModal(true)}
            >
              <FontAwesomeIcon icon={['far', 'user']} className="fs-13 m-right-8" color="#1769AF" />
              <span className="fw-600 fs-13" style={{ color: '#1769AF' }}>
                Assign Associate
              </span>
            </button>
            <button
              className="flex-row-nospacebetwee-nowrap border-none pointer"
              style={{ alignItems: 'center', backgroundColor: '#fff' }}
              onClick={handleComplete}
            >
              <FontAwesomeIcon icon={['far', 'check-circle']} className="fs-16 m-right-8" color="#1769AF" />
              <span className="fw-600 fs-13" style={{ color: '#1769AF' }}>
                Complete
              </span>
            </button>
          </div>
        ) : null
      }
    >
      {isFetching ? (
        <div className="w-100-P flex-col-center m-top-10">
          <Spin tip="Loading..." size="large" indicator={<LoadingOutlined spin />}>
            <div
              style={{
                padding: 50,
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 8,
              }}
            />
          </Spin>
        </div>
      ) : (
        <AppointmentTable
          data={data}
          appointmentsConfig={appointmentsConfig}
          hideColumns={['associate']}
          formatDate="h:mmaaa MMM dd, yyyy"
          handleEdit={handleEdit}
          handleMessage={openChatModal}
          tableProps={{
            pagination: false,
            rowKey: item => item.id,
            rowSelection: {
              selectedRowKeys,
              onChange: handleSelectedRowKeysChange,
              getCheckboxProps: record => ({ disabled: !isEditable(record.status) }),
            },
          }}
        />
      )}
      <AddEditAppointment
        isOpen={openEditModal}
        onClose={handleCloseModal}
        appointment={selectedAppointment}
        onSuccess={refetch}
        allAssoc={allAssoc}
      />
      <AssignAssociatesModal
        isOpen={openAssignModal}
        onClose={() => setOpenAssignModal(false)}
        onAssign={handleAssign}
        allAssoc={allAssoc.filter(assoc => selectedStoreRow.every(storeId => assoc.store_ids.includes(storeId)))}
        warning={
          selectedStoreRow.length > 1
            ? "You've selected appointments from multiple stores. Options are limited to associates assigned to ALL selected stores."
            : null
        }
      />
    </Section>
  );
};

const mapStateToProps = state => ({ auth: state.auth, messaging: state.messaging });
export default connect(mapStateToProps, {})(TodayAppointments);
